/* eslint-disable react/jsx-no-undef */
import React, { FC, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { DataBox } from '../../components/DataBox/DataBox';
import { Loader } from '../../components/Loader/Loader';
import { DeliveryInfo, fetchDelivery } from '../../fetch/orderFetch';
import './Delivery.scss';
import { DeliveryRating } from './DeliveryRating';

interface MatchParams {
  id: string;
}

export interface RouteComponentProps<P> {
  match: Match<P>;
}

export interface Match<P> {
  params: P;
  isExact: boolean;
  path: string;
  url: string;
}

export const DeliveryComponent: FC<RouteComponentProps<MatchParams>> = ({
  match,
}) => {
  const deliveryId = match.params.id;
  const [deliveryInfo, setDeliveryInfo] = useState<DeliveryInfo>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const getDelivery = async (deliveryId: string) => {
    if (deliveryId) {
      setDeliveryInfo(await fetchDelivery(deliveryId));
    } else {
      setDeliveryInfo(null);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    getDelivery(deliveryId);
  }, [deliveryId]);

  return (
    <div className="delivery-wrapper">
      <Helmet>
        <title>Delivery - {deliveryId}</title>
      </Helmet>
      {isLoading && (
        <div className="delivery-loader">
          <Loader type="spinner" size="xxl" />
        </div>
      )}
      {deliveryInfo ? (
        <div className="delivery">
          <h2>Delivery information</h2>

          <div className="delivery__columns">
            <div className="delivery__information">
              <div className="delivery__section">
                <div className="delivery__row">
                  <p>Delivery ID:</p>
                  <p>{deliveryId}</p>
                </div>
                <div className="delivery__row">
                  <p>Delivery token:</p>
                  <p>{deliveryInfo.delivery.token}</p>
                </div>
                <div className="delivery__row hidden">
                  <p>Delivery pin:</p>
                  <p>{deliveryInfo.delivery.deliveryPin}</p>
                </div>
                <div className="delivery__row">
                  <p>Recipient email:</p>
                  <p>{deliveryInfo.delivery.recipientEmail}</p>
                </div>
              </div>

              <div className="delivery__section">
                <DeliveryRating
                  deliveryToken={deliveryInfo.delivery.token}
                  rating={deliveryInfo.delivery.deliveryRating}
                  feedback={deliveryInfo.delivery.feedback}
                  feedbackFlags={deliveryInfo.delivery.feedbackFlags}
                  refetchData={() => getDelivery(deliveryId)}
                />
              </div>

              <DataBox title="Delivery instruction">
                <dl className="definitionlist">
                  <dt>Confirmed contactless:</dt>
                  <dd>
                    {deliveryInfo.delivery.confirmedContactless ? 'yes' : 'no'}
                  </dd>
                  {deliveryInfo.delivery.userInstructions && (
                    <>
                      <dt>Residence type:</dt>
                      <dd>
                        {deliveryInfo.delivery.userInstructions.residenceType ||
                          '-'}
                      </dd>
                      <dt>Floor:</dt>
                      <dd>
                        {deliveryInfo.delivery.userInstructions.floor || '-'}
                      </dd>
                      <dt>Name on doorbell:</dt>
                      <dd>
                        {deliveryInfo.delivery.userInstructions.doorbellName ||
                          '-'}
                      </dd>
                      <dt>Notification type:</dt>
                      <dd>
                        {deliveryInfo.delivery.userInstructions
                          .notificationType || '-'}
                      </dd>
                      <dt>Notes:</dt>
                      <dd>
                        {deliveryInfo.delivery.userInstructions.note || '-'}
                      </dd>
                    </>
                  )}
                </dl>
              </DataBox>
            </div>

            <div className="delivery__orders">
              <h5>Orders in this delivery</h5>
              {deliveryInfo.orderIds.length > 0 ? (
                <ul>
                  {deliveryInfo.orderIds.map((orderId, i) => (
                    <li key={i}>
                      <Link to={`/orders/${orderId}`} className="link__clean">
                        {orderId}
                      </Link>
                    </li>
                  ))}
                </ul>
              ) : (
                <p>Could not find any orders related to this delivery</p>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div
          style={{
            margin: '0px auto',
            paddingTop: 20,
            textAlign: 'center',
          }}
        >
          <h3>No delivery with id {deliveryId}</h3>
        </div>
      )}
    </div>
  );
};
